import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Skeleton from "@mui/material/Skeleton";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import DonutLargeRoundedIcon from "@mui/icons-material/DonutLargeRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import EventBusyRoundedIcon from "@mui/icons-material/EventBusyRounded";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";

import moment from "moment";

import Langs, { Localize } from "../../app/lang/langs";
import { showErrorToast } from "../../app/utils/helpers";
import {
    exportVLearnDashboard, getVLearnCourse, getVLearnDashboard,
    getVLearnLearners, getVLearnLessonStats
} from "./vlearnSlice";
import {
    VLEARN_STATUS_STYLES, VLearnEmptyState, VLearnStatTile,
    vlearnGrid, vlearnSurface, vlearnType
} from "../../components/vlearn/vlearnUi";
import LearnerProgressTable from "../../components/vlearn/dashboard/LearnerProgressTable";
import LessonStatsTable from "../../components/vlearn/dashboard/LessonStatsTable";
import ActivityFeed from "../../components/vlearn/dashboard/ActivityFeed";

/**
 * The teacher dashboard — who did well, who did not, and who saw what.
 *
 * Three endpoints feed it, all of them re-checking `canViewDashboard` server-side:
 * `getCourseDashboard` for the KPI header, `getLearnerProgress` for the learner table and
 * `getLessonStats` for the per-lesson view. The activity feed fetches itself, because its
 * filters and its paging belong to the tab rather than to this screen.
 *
 * ── Every rate carries its denominator ───────────────────────────────────────────────
 *
 * A tile reading "68%" is not a finding — it is a number waiting for the question "out of how
 * many". Sixty-eight percent of twenty-five people is a training gap worth chasing; the same
 * figure over three people is noise. So each rate tile states its basis in the `hint` slot the
 * shared `VLearnStatTile` keeps for exactly this, and the two quiz tiles say plainly when
 * nothing has been graded rather than printing a zero that reads as failure.
 *
 * @param {object} props
 * @param {number} props.idCourse
 * @param {() => void} props.onBack
 * @param {() => void} [props.onOpenCourse] Opens the course itself, for a reader who arrived
 *   at the numbers and now wants the material.
 */
export default function CourseDashboard({ idCourse, onBack, onOpenCourse }) {

    const dispatch = useDispatch()

    const course = useSelector((state) => state.vlearn.course)
    const access = useSelector((state) => state.vlearn.access)
    const summary = useSelector((state) => state.vlearn.dashboardSummary)
    const loading = useSelector((state) => state.vlearn.dashboardLoading)
    const lang = useSelector((state) => state.settings.lang)

    const [tab, setTab] = useState(0)

    // Keyed on the course rather than a bare boolean: React 18's StrictMode mounts every
    // component twice in development, and a bare `mounted` flag would also stop the screen
    // refetching when the reader moves to another course without unmounting it.
    const fetchedFor = useRef(null)

    useEffect(() => {
        if (!idCourse) return
        if (fetchedFor.current === idCourse) return
        fetchedFor.current = idCourse

        /*
         * THE COURSE MUST BE FETCHED HERE, and this screen is the only course-scoped one that
         * was not doing it.
         *
         * It reads `state.vlearn.course` and `state.vlearn.access`, and used to assume both
         * were already in the store from whichever screen the user arrived from. But both
         * entry points — CoursePlayer and CourseEditor — dispatch `closeCourse()` in their
         * unmount cleanup, which runs in the same commit that mounts this component. So the
         * store was always empty by the time it settled, `course` stayed null forever, and the
         * dashboard rendered its loading skeleton permanently with no title and no way back.
         *
         * The access guard cannot come before this either: `access` is all-false until this
         * very request lands, so gating the fetch on it was a deadlock.
         */
        dispatch(getVLearnCourse({ id_course: idCourse }))
        dispatch(getVLearnDashboard({ id_course: idCourse }))
        // The learner table owns its own paging and filtering afterwards; this is only the
        // first page, so the tab has rows the moment it is opened.
        dispatch(getVLearnLearners({ id_course: idCourse, limit: 25, offset: 0 }))
        dispatch(getVLearnLessonStats({ id_course: idCourse }))
    }, [dispatch, idCourse])

    const onExport = useCallback(async () => {
        // Built from the course title so a folder of exports is readable, and folded to ASCII
        // because this string becomes a filename on the reader's own filesystem — an accent or
        // a slash in it is at best mangled and at worst a path the browser refuses.
        const slug = String((course && course.title) || idCourse)
            .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
            .replace(/[^A-Za-z0-9_-]/g, '_')
            .replace(/_+/g, '_')
            .replace(/^_|_$/g, '')
            .slice(0, 60) || String(idCourse)
        const today = moment().format('YYYY-MM-DD')

        const response = await dispatch(exportVLearnDashboard({
            idCourse,
            filename: `VLearn_${slug}_${today}.xlsx`
        }))
        // The export thunk resolves with `responseError` on failure rather than rejecting,
        // which is the convention every thunk in this slice follows.
        if (response.payload && response.payload.responseError) {
            showErrorToast(Localize("VLEARN_EXPORT_FAILED", lang))
        }
    }, [dispatch, idCourse, course, lang])

    /**
     * The three mutually exclusive buckets, derived from the summary's own totals.
     *
     * `_buildCourseSummary` returns `enrolled`, `started` and `completed`, where `started`
     * counts everyone past `not_started` — completed people included. Subtracting gives the
     * three-way split without a fourth query, and keeps the segments adding up to `enrolled`
     * by construction.
     */
    const distribution = useMemo(() => {
        if (!summary) return null
        const enrolled = Number(summary.enrolled) || 0
        const started = Number(summary.started) || 0
        const completed = Number(summary.completed) || 0
        return {
            enrolled,
            segments: [
                {
                    status: 'not_started',
                    count: Math.max(0, enrolled - started),
                    labelKey: 'VLEARN_STATUS_NOT_STARTED'
                },
                {
                    status: 'in_progress',
                    count: Math.max(0, started - completed),
                    labelKey: 'VLEARN_STATUS_IN_PROGRESS'
                },
                {
                    status: 'completed',
                    count: completed,
                    labelKey: 'VLEARN_STATUS_COMPLETED'
                }
            ]
        }
    }, [summary])

    // ── The access gate ─────────────────────────────────────────────────────────────
    //
    // `access` is the backend's own answer for the open course, filled in by
    // `getVLearnCourse`. Until that has landed it is still the initial all-false object, so a
    // denial drawn before then would be a guess — hence the wait on `course` rather than an
    // immediate refusal.
    if (!course) {
        return (
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
                <Skeleton width={280} height={34} />
                <Box sx={{ ...vlearnGrid.tiles, mt: 2.5 }}>
                    {Array.from({ length: 8 }).map((_, index) => (
                        <Skeleton key={index} variant="rectangular" height={96}
                            sx={{ borderRadius: '14px' }} />
                    ))}
                </Box>
            </Box>
        )
    }

    if (!access.canViewDashboard) {
        return (
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ ...vlearnSurface.card }}>
                    <VLearnEmptyState
                        icon={<LockOutlinedIcon />}
                        title={<Langs str="VLEARN_NO_DASHBOARD_ACCESS_TITLE" />}
                        description={<Langs str="VLEARN_NO_DASHBOARD_ACCESS_BODY" />}
                        action={
                            <Button variant="outlined" size="small" onClick={onBack}
                                startIcon={<ArrowBackRoundedIcon />}
                                sx={{ textTransform: 'none' }}>
                                <Langs str="VLEARN_BACK" />
                            </Button>
                        }
                    />
                </Box>
            </Box>
        )
    }

    const enrolled = summary ? Number(summary.enrolled) || 0 : 0
    const hasQuizResults = Boolean(summary && Number(summary.quiz_attempts_graded) > 0)

    /** A rate value, or an em dash when the server said "no basis for this yet" with null. */
    const rate = (value) => (value === null || typeof value === 'undefined')
        ? '—' : `${value}%`

    return (
        <Box sx={{
            display: 'flex', flexDirection: 'column', gap: 3,
            p: { xs: 2, sm: 3 },
            // The page itself never scrolls sideways; each table scrolls inside its own
            // container instead.
            maxWidth: '100%', overflowX: 'hidden'
        }}>

            {/* ── Header ──────────────────────────────────────────────────────────── */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flexWrap: 'wrap' }}>
                <IconButton
                    onClick={onBack}
                    size="small"
                    aria-label={Localize("VLEARN_BACK", lang)}
                    sx={{
                        color: 'var(--orange, rgb(255,144,21))',
                        // `size="small"` is a 30px square. This is the only way out of the
                        // dashboard, so it gets the 44px tap-target floor.
                        minWidth: 44, minHeight: 44,
                        '&:focus-visible': {
                            outline: '2px solid var(--orange, rgb(255,144,21))', outlineOffset: 2
                        }
                    }}
                >
                    <ArrowBackRoundedIcon />
                </IconButton>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography component="h1" sx={{ ...vlearnType.pageTitle }}>
                        {course.title}
                    </Typography>
                    <Typography sx={{ ...vlearnType.body, mt: 0.25 }}>
                        <Langs str="VLEARN_DASHBOARD_SUBTITLE" />
                    </Typography>
                </Box>

                {/* Full width below `sm`: at 360px this pair shares the title's line, wraps,
                    and lands as two 30px buttons in a corner. Their own row, at the tap-target
                    floor, is the same amount of screen and reachable. */}
                <Box sx={{
                    display: 'flex', gap: 1, alignItems: 'center',
                    width: { xs: '100%', sm: 'auto' },
                    '& > .MuiButton-root': { minHeight: 44, flex: { xs: 1, sm: 'none' } }
                }}>
                    {onOpenCourse ? (
                        <Tooltip title={Localize("VLEARN_OPEN_COURSE", lang)} arrow>
                            <Button
                                variant="text"
                                size="small"
                                onClick={onOpenCourse}
                                startIcon={<OpenInNewRoundedIcon />}
                                sx={{ textTransform: 'none' }}
                            >
                                <Langs str="VLEARN_OPEN_COURSE" />
                            </Button>
                        </Tooltip>
                    ) : null}
                    <Button
                        variant="contained"
                        size="small"
                        onClick={onExport}
                        startIcon={<FileDownloadRoundedIcon />}
                        sx={{ textTransform: 'none' }}
                    >
                        <Langs str="VLEARN_EXPORT" />
                    </Button>
                </Box>
            </Box>

            {/* ── KPI row ─────────────────────────────────────────────────────────── */}
            <Box component="section" sx={{ ...vlearnGrid.tiles }}>
                {(loading && !summary) || !summary ? (
                    Array.from({ length: 8 }).map((_, index) => (
                        <Skeleton key={index} variant="rectangular" height={96}
                            sx={{ borderRadius: '14px' }} />
                    ))
                ) : (
                    <>
                        <VLearnStatTile
                            label={<Langs str="VLEARN_KPI_ENROLLED" />}
                            value={summary.enrolled}
                            icon={<GroupsOutlinedIcon />}
                            hint={<Langs
                                str={enrolled === 1 ? "VLEARN_HINT_ENROLLED_ONE" : "VLEARN_HINT_ENROLLED_MANY"}
                                params={{ count: enrolled }} />}
                        />
                        <VLearnStatTile
                            label={<Langs str="VLEARN_KPI_STARTED" />}
                            value={summary.started}
                            icon={<PlayCircleOutlineIcon />}
                            accent={VLEARN_STATUS_STYLES.in_progress.bar}
                            hint={<Langs str="VLEARN_HINT_OUT_OF_ENROLLED"
                                params={{ value: summary.started, total: enrolled }} />}
                        />
                        <VLearnStatTile
                            label={<Langs str="VLEARN_KPI_COMPLETED" />}
                            value={summary.completed}
                            icon={<TaskAltIcon />}
                            accent={VLEARN_STATUS_STYLES.completed.bar}
                            hint={<Langs str="VLEARN_HINT_OUT_OF_ENROLLED"
                                params={{ value: summary.completed, total: enrolled }} />}
                        />
                        <VLearnStatTile
                            label={<Langs str="VLEARN_KPI_COMPLETION_RATE" />}
                            value={rate(summary.completion_rate)}
                            icon={<DonutLargeRoundedIcon />}
                            accent={VLEARN_STATUS_STYLES.completed.bar}
                            hint={enrolled > 0
                                ? <Langs str="VLEARN_HINT_COMPLETION_BASIS"
                                    params={{ done: summary.completed, total: enrolled }} />
                                : <Langs str="VLEARN_HINT_NO_ENROLLED" />}
                        />
                        <VLearnStatTile
                            label={<Langs str="VLEARN_KPI_AVERAGE_PROGRESS" />}
                            value={rate(summary.average_progress)}
                            icon={<TrendingUpRoundedIcon />}
                            hint={enrolled > 0
                                ? <Langs str="VLEARN_HINT_AVERAGE_BASIS" params={{ total: enrolled }} />
                                : <Langs str="VLEARN_HINT_NO_ENROLLED" />}
                        />
                        <VLearnStatTile
                            label={<Langs str="VLEARN_KPI_OVERDUE" />}
                            value={summary.overdue}
                            icon={<EventBusyRoundedIcon />}
                            accent={VLEARN_STATUS_STYLES.overdue.bar}
                            hint={<Langs str="VLEARN_HINT_OVERDUE_BASIS" params={{ total: enrolled }} />}
                        />
                        <VLearnStatTile
                            label={<Langs str="VLEARN_KPI_QUIZ_SCORE" />}
                            value={rate(summary.average_quiz_score)}
                            icon={<QuizOutlinedIcon />}
                            accent="#8E4EC6"
                            hint={hasQuizResults
                                ? <Langs str="VLEARN_HINT_QUIZ_BASIS"
                                    params={{ graded: summary.quiz_attempts_graded }} />
                                : <Langs str="VLEARN_HINT_QUIZ_NONE" />}
                        />
                        <VLearnStatTile
                            label={<Langs str="VLEARN_KPI_QUIZ_PASS_RATE" />}
                            value={rate(summary.quiz_pass_rate)}
                            icon={<FactCheckOutlinedIcon />}
                            accent="#6D28D9"
                            hint={hasQuizResults
                                ? <Langs str="VLEARN_HINT_QUIZ_BASIS"
                                    params={{ graded: summary.quiz_attempts_graded }} />
                                : <Langs str="VLEARN_HINT_QUIZ_NONE" />}
                        />
                    </>
                )}
            </Box>

            {/* ── Distribution ────────────────────────────────────────────────────── */}
            {/*
              * A stacked bar built from CSS boxes rather than a charting library — the project
              * takes no new dependency for this, and three proportions do not need one.
              *
              * Each segment states its own count next to its own name, and the legend below
              * repeats both. Colour orders the segments and nothing more: the bar is still
              * readable in greyscale, at 200% zoom, and to a screen reader through the
              * `aria-label` on the group.
              */}
            {distribution && distribution.enrolled > 0 ? (
                <Box component="section" sx={{ ...vlearnSurface.panel }}>
                    <Typography component="h2" sx={{ ...vlearnType.sectionTitle, mb: 1.5 }}>
                        <Langs str="VLEARN_DISTRIBUTION_TITLE" />
                    </Typography>

                    <Box
                        role="img"
                        aria-label={distribution.segments
                            .map((segment) =>
                                `${Localize(segment.labelKey, lang)}: ${segment.count}`)
                            .join(', ')}
                        sx={{
                            display: 'flex', width: '100%', height: 30,
                            borderRadius: '8px', overflow: 'hidden',
                            backgroundColor: 'rgba(0,0,0,0.05)'
                        }}
                    >
                        {distribution.segments.map((segment) => {
                            if (segment.count <= 0) return null
                            const share = (segment.count / distribution.enrolled) * 100
                            const style = VLEARN_STATUS_STYLES[segment.status]
                            return (
                                <Tooltip
                                    key={segment.status}
                                    arrow
                                    title={`${Localize(segment.labelKey, lang)} — ${segment.count} / ${distribution.enrolled}`}
                                >
                                    <Box
                                        sx={{
                                            width: `${share}%`,
                                            backgroundColor: style.bar,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            // A very thin segment cannot hold its own number;
                                            // the legend underneath carries it in that case.
                                            overflow: 'hidden',
                                            transition: 'width .35s ease',
                                            '@media (prefers-reduced-motion: reduce)': {
                                                transition: 'none'
                                            }
                                        }}
                                    >
                                        <Typography sx={{
                                            fontSize: '0.72rem', fontWeight: 700, color: '#fff',
                                            fontVariantNumeric: 'tabular-nums',
                                            textShadow: '0 1px 2px rgba(0,0,0,0.25)',
                                            whiteSpace: 'nowrap'
                                        }}>
                                            {segment.count}
                                        </Typography>
                                    </Box>
                                </Tooltip>
                            )
                        })}
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap', mt: 1.25 }}>
                        {distribution.segments.map((segment) => {
                            const style = VLEARN_STATUS_STYLES[segment.status]
                            const share = Math.round(
                                (segment.count / distribution.enrolled) * 1000) / 10
                            return (
                                <Box key={segment.status}
                                    sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                                    <Box aria-hidden="true" sx={{
                                        width: 10, height: 10, borderRadius: '3px',
                                        backgroundColor: style.bar, flexShrink: 0
                                    }} />
                                    <Typography sx={{
                                        ...vlearnType.meta, fontVariantNumeric: 'tabular-nums'
                                    }}>
                                        {`${Localize(segment.labelKey, lang)} — ${segment.count} (${share}%)`}
                                    </Typography>
                                </Box>
                            )
                        })}
                    </Box>
                </Box>
            ) : null}

            {/* ── Tabs ────────────────────────────────────────────────────────────── */}
            <Box component="section">
                <Box sx={{ borderBottom: '1px solid rgba(0,0,0,0.08)', mb: 2 }}>
                    <Tabs
                        value={tab}
                        onChange={(event, value) => setTab(value)}
                        // Scrollable rather than squeezed: three French tab labels do not fit
                        // 328px, and MUI's default is to shrink them until they wrap inside
                        // their own indicator.
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                        sx={{
                            '& .MuiTab-root': {
                                textTransform: 'none', fontWeight: 600, fontSize: '0.875rem',
                                minHeight: 46,
                                '&:focus-visible': {
                                    outline: '2px solid var(--orange, rgb(255,144,21))',
                                    outlineOffset: -2
                                }
                            },
                            '& .Mui-selected': {
                                color: 'var(--orange, rgb(255,144,21))', fontWeight: 700
                            },
                            '& .MuiTabs-indicator': {
                                backgroundColor: 'var(--orange, rgb(255,144,21))',
                                height: 3, borderRadius: '3px 3px 0 0'
                            }
                        }}
                    >
                        <Tab label={Localize("VLEARN_TAB_LEARNERS", lang)}
                            id="vlearn-tab-learners" aria-controls="vlearn-panel-learners" />
                        <Tab label={Localize("VLEARN_TAB_LESSONS", lang)}
                            id="vlearn-tab-lessons" aria-controls="vlearn-panel-lessons" />
                        <Tab label={Localize("VLEARN_TAB_ACTIVITY", lang)}
                            id="vlearn-tab-activity" aria-controls="vlearn-panel-activity" />
                    </Tabs>
                </Box>

                {/*
                  * Only the active panel is mounted. Each tab owns requests of its own — the
                  * feed pages, the learner table filters — and mounting all three would fire
                  * every one of them on arrival for two panels nobody is looking at.
                  */}
                {tab === 0 ? (
                    <Box role="tabpanel" id="vlearn-panel-learners" aria-labelledby="vlearn-tab-learners">
                        <LearnerProgressTable idCourse={idCourse} />
                    </Box>
                ) : null}

                {tab === 1 ? (
                    <Box role="tabpanel" id="vlearn-panel-lessons" aria-labelledby="vlearn-tab-lessons">
                        <LessonStatsTable idCourse={idCourse} />
                    </Box>
                ) : null}

                {tab === 2 ? (
                    <Box role="tabpanel" id="vlearn-panel-activity" aria-labelledby="vlearn-tab-activity">
                        <ActivityFeed idCourse={idCourse} />
                    </Box>
                ) : null}
            </Box>
        </Box>
    )
}
